<script setup lang="ts">
import messages from './FourthStep.locale.json';
import type { FourthStepProps } from '.';
import yup from '~/core/validation';

const { t } = useI18n({
	useScope: 'local',
	messages
});

const props = withDefaults(defineProps<FourthStepProps>(), {
	isLoading: false
});

interface Emits {
	(e: 'validated', params: { name: string; email?: string }): void;
}

const emit = defineEmits<Emits>();

const schema = yup.object({
	email: yup.string().email(),
	name: yup
		.string()
		.when((_, schema) => (props.context === 'sms' ? schema.required() : schema))
});

const { defineField, handleSubmit, errors, resetForm, meta } = useForm({
	validationSchema: schema
});

const [email, emailAttrs] = defineField('email', {
	validateOnModelUpdate: true
});

const [name, nameAttrs] = defineField('name', {
	validateOnModelUpdate: true
});

const submit = handleSubmit(() => {
	emit('validated', { email: email.value, name: name.value });
});

onUnmounted(() => {
	resetForm();
});
</script>

<template>
	<section class="flex flex-col space-y-6" data-testid="fourth-step">
		<div class="flex flex-col gap-4">
			<Input
				v-model="name"
				v-bind="nameAttrs"
				:autofocus="isActive"
				:placeholder="t('form.name.label')"
				:error="errors?.name"
				class="h-[50px]"
			/>

			<Input
				v-model="email"
				v-bind="emailAttrs"
				:placeholder="t('form.email.label')"
				:error="errors?.email"
				class="h-[50px]"
			/>
		</div>

		<Button class="h-12" :disabled="!meta.valid || isLoading" @click="submit">
			<IconLineMdLoadingTwotoneLoop v-if="isLoading" class="size-[1.7rem]" />
			{{ t('submitButton') }}
		</Button>
	</section>
</template>
